import React, { useEffect, useRef } from 'react';
import { classnames } from '@/utils/classnames/classnames';
import { useTabs } from '../../context';
import { TabListProps } from './types';
import { TabProps } from '../tab/types';

const TabList = ({ children, variant = 'underline', className }: React.PropsWithChildren<TabListProps>) => {
  const { activeTabIndex, setActiveTabIndex, onActiveIndexChange, baseId } = useTabs();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (variant !== 'pill') return;
    const container = listRef.current;
    const activePill = container?.querySelector<HTMLButtonElement>(`[data-tab-index="${activeTabIndex}"]`);
    if (!container || !activePill) return;
    // Centre the active pill within its own horizontal track by scrolling the
    // container only. Deliberately NOT scrollIntoView: that scrolls every
    // scrollable ancestor including the document, so firing it on mount dragged
    // the whole page down to the FAQ section on load (EWM-780).
    const containerRect = container.getBoundingClientRect();
    const pillRect = activePill.getBoundingClientRect();
    const targetLeft =
      container.scrollLeft + (pillRect.left - containerRect.left) - (container.clientWidth - pillRect.width) / 2;
    container.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
  }, [activeTabIndex, variant]);

  const buttons = React.Children.map(children, (Child, index) => {
    const isActive = index === activeTabIndex;
    // EWM-956 — a pill tab may override its resting (inactive) treatment with a
    // design-system variant's tokens (e.g. Button `secondary`). Falls back to
    // the default plain-text inactive style. Only affects the pill variant.
    const pillRestingClassName =
      React.isValidElement<TabProps>(Child) && Child.props.pillRestingClassName
        ? Child.props.pillRestingClassName
        : 'text-gray-600 hover:text-primary hover:bg-gray-50';
    const tabClassName =
      variant === 'pill'
        ? classnames(
            'shrink-0 cursor-pointer rounded-full px-5 py-2.5 whitespace-pre text-13 font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            isActive ? 'bg-primary text-white' : pillRestingClassName,
          )
        : classnames(
            'mb-[-2px] cursor-pointer whitespace-pre border-b-2 py-2 leading-[16px] transition hover:border-gray-800',
            {
              'border-transparent': !isActive,
              'border-gray-800 font-bold': isActive,
            },
          );

    return (
      <button
        key={index}
        id={`${baseId}-tab-${index}`}
        role="tab"
        type="button"
        aria-selected={isActive}
        aria-controls={`${baseId}-panel-${index}`}
        tabIndex={isActive ? 0 : -1}
        data-tab-index={index}
        className={tabClassName}
        onClick={() => {
          setActiveTabIndex(index);
          onActiveIndexChange?.(index);
        }}
      >
        {Child}
      </button>
    );
  });

  // Pill variant: outer div handles scroll/centering; inner pill track
  // (`role="tablist"`) matches Dan's mock — bordered rounded container.
  if (variant === 'pill') {
    return (
      <div ref={listRef} className={classnames('scrollbar-hide flex overflow-x-auto px-4 sm:px-0', className)}>
        <div
          role="tablist"
          className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1.5"
        >
          {buttons}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      className={classnames(
        'flex items-center gap-5 overflow-visible border-b-2 border-neutral-400 lg:gap-10',
        className,
      )}
    >
      {buttons}
    </div>
  );
};

export default TabList;
